import { computed, ComputedRef } from 'vue';
import { useLayoutStore } from '@/stores/layoutStore';
import { useUserDefinedIndexes } from '@/composables/useUserDefinedIndexes';
import { removeDuplicatesFromArray } from '@/utils/removeDuplicatesFromArray';
import { getNumberOfUniqueValuesInArray } from '@/utils/getNumberOfUniqueValuesInArray';
import { PromiseCacheMap, SizeCacheMap } from '@/utils/cache-map';
import { getPeriodOptionFromPeriodValue } from '@/enums/period-options';
import { SymbolInformation } from '@/interfaces/SymbolInformation';
import { JsonLayerResponse, isJsonLayerResponse } from '@/interfaces/jsonLayerResponse';

const ONE_SECOND = 1000;
const MAX_NUM_CACHE_ENTRIES = 5000;
const REMOVE_OLD_CACHE_ENTRIES_LOOP_FREQUENCY = 40 * ONE_SECOND;

const symbolInformationPromisesCache = new PromiseCacheMap<void>();
const symbolInformationCache = new SizeCacheMap<SymbolInformation>(REMOVE_OLD_CACHE_ENTRIES_LOOP_FREQUENCY, MAX_NUM_CACHE_ENTRIES);

function getSymbolInformationFromCache(symbol: string): SymbolInformation | null {
  if (!isValidSymbol(symbol)) {
    return null;
  }

  symbol = symbol.trim().toUpperCase();

  const individualSymbols = splitRatioAndDifferenceSymbolsIntoParts(symbol);

  if (!individualSymbols.length) {
    return null;
  }

  const symbolInformationArray: SymbolInformation[] = [];

  for (const individualSymbol of individualSymbols) {
    const cachedSymbolInformation = symbolInformationCache.get(individualSymbol, true);

    if (!cachedSymbolInformation) {
      return null;
    }

    symbolInformationArray.push(cachedSymbolInformation);
  }

  return mergeSymbolInformationForRatioOrDifferenceSymbol(symbolInformationArray);
}

interface JsonLayerCompanyInformation {
  symbol: string;
  companyInfo: {
    exchange: string;
    isEOD: boolean;
    name: string;
    marketRegion?: string | null;
  };
};

function isJsonLayerCompanyInformation(unknownValue: unknown): unknownValue is JsonLayerCompanyInformation {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'symbol' in unknownValue &&
    'companyInfo' in unknownValue &&
    typeof unknownValue.symbol === 'string' &&
    typeof unknownValue.companyInfo === 'object' &&
    unknownValue.companyInfo !== null &&
    'exchange' in unknownValue.companyInfo &&
    'isEOD' in unknownValue.companyInfo &&
    'name' in unknownValue.companyInfo &&
    typeof unknownValue.companyInfo.exchange === 'string' &&
    typeof unknownValue.companyInfo.isEOD === 'boolean' &&
    typeof unknownValue.companyInfo.name === 'string' &&
    (!('marketRegion' in unknownValue.companyInfo) || typeof unknownValue.companyInfo.marketRegion === 'string' || unknownValue.companyInfo.marketRegion === null)
  );
}

interface FetchSymbolInformationResponse extends JsonLayerResponse {
  companiesInfo: unknown[];
}

function isFetchSymbolInformationResponse(jsonLayerResponse: JsonLayerResponse): jsonLayerResponse is FetchSymbolInformationResponse {
  return (
    'companiesInfo' in jsonLayerResponse &&
    Array.isArray(jsonLayerResponse.companiesInfo)
  );
}

function populateSymbolInformationCache(symbols: string[], jsonLayerResponse: JsonLayerResponse): void {
  const { removeChartCodeFromUserDefinedIndexSymbol } = useUserDefinedIndexes();

  if (!isFetchSymbolInformationResponse(jsonLayerResponse)) {
    window.sc3WorkbenchLogger.warn(`jsonLayerResponse.success is true but jsonLayerResponse does not match FetchSymbolInformationResponse interface - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
    return;
  }

  if (jsonLayerResponse.companiesInfo.length !== symbols.length) {
    window.sc3WorkbenchLogger.warn(`json-layer get-companies-info command returned ${jsonLayerResponse.companiesInfo.length} companyInfo object${jsonLayerResponse.companiesInfo.length > 1 ? 's' : ''} but ${symbols.length} symbols ${symbols.length > 1 ? 'were' : 'was'} requested.`);
  }

  for (let i = 0; i < jsonLayerResponse.companiesInfo.length; i++) {
    const jsonLayerCompanyInformation = jsonLayerResponse.companiesInfo[i];

    if (!isJsonLayerCompanyInformation(jsonLayerCompanyInformation)) {
      window.sc3WorkbenchLogger.warn(`Failed to fetch symbol information - jsonLayerResponse.companiesInfo[${i}] does not match JsonLayerCompanyInformation interface.`);
      continue;
    }

    const symbol = removeChartCodeFromUserDefinedIndexSymbol(jsonLayerCompanyInformation.symbol.trim().toUpperCase());
    const { companyInfo } = jsonLayerCompanyInformation;

    if (!symbol) {
      window.sc3WorkbenchLogger.warn(`Failed to fetch symbol information - trimmed jsonLayerResponse.companiesInfo[${i}].symbol is an empty string.`);
      continue;
    }

    if (!companyInfo.exchange.trim()) {
      window.sc3WorkbenchLogger.warn(`Failed to fetch symbol information for symbol: ${symbol} - trimmed jsonLayerResponse.companiesInfo[${i}].companyInfo.exchange is an empty string.`);
      continue;
    }

    const symbolInformation: SymbolInformation = {
      exchange: companyInfo.exchange.trim(),
      isEndOfDay: companyInfo.isEOD,
      name: companyInfo.name.trim()
    };

    if (typeof companyInfo.marketRegion === 'string' && companyInfo.marketRegion.trim()) {
      symbolInformation.marketRegion = companyInfo.marketRegion.trim();
    }

    symbolInformationCache.set(symbol, symbolInformation);
  }
}

async function fetchSymbolInformation(symbols: string[], isRetrying?: boolean): Promise<void> {
  const { appendChartCodeToUserDefinedIndexSymbol } = useUserDefinedIndexes();

  if (!symbols.length) {
    return;
  }

  const symbolsString = symbols.map(appendChartCodeToUserDefinedIndexSymbol).join(',');
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'get-companies-info',
        symbols: symbolsString
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    if (!isJsonLayerResponse(jsonLayerResponse)) {
      throw new Error('jsonLayerResponse does not match JsonLayerResponse interface.');
    }

    if (!jsonLayerResponse.success) {
      throw new Error(`jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
    }
  } catch (e) {
    if (isRetrying) {
      window.sc3WorkbenchLogger.warn(`Failed to fetch symbol information for symbols: ${symbolsString}.`, e);
      return;
    }

    return fetchSymbolInformation(symbols, true);
  }

  populateSymbolInformationCache(symbols, jsonLayerResponse);
}

function splitRatioAndDifferenceSymbolsIntoParts(compositeSymbol: string): string[] {
  if (typeof compositeSymbol !== 'string') {
    return [];
  }

  const individualSymbols = compositeSymbol
    .replace(/\s/g, '')
    .toUpperCase()
    .split(/[:-]/)
    .filter(s => !!s);

  return removeDuplicatesFromArray(individualSymbols);
}

let individualSymbolsBeingViewed: ComputedRef<string[]> | undefined;
let symbolInformationArrayForSymbolBeingViewed: ComputedRef<(SymbolInformation|null)[]> | undefined;
let symbolInformationForSymbolBeingViewed: ComputedRef<SymbolInformation | null> | undefined;
let isSymbolBeingViewedNotFound: ComputedRef<boolean> | undefined;
let isSymbolBeingViewedAnEODSymbolOnAnIntradayChart: ComputedRef<boolean> | undefined;

function mergeSymbolInformationForRatioOrDifferenceSymbol(symbolInformationArray: SymbolInformation[]): SymbolInformation {
  if (symbolInformationArray.length === 1) {
    return { ...symbolInformationArray[0] };
  }

  const individualNames = symbolInformationArray.map(symbolInformation => symbolInformation.name);
  const combinedName = individualNames.join('/');

  const individualExchanges = symbolInformationArray.map(symbolInformation => symbolInformation.exchange);
  const combinedExchange = getNumberOfUniqueValuesInArray(individualExchanges) === 1 ? individualExchanges[0] : individualExchanges.join('/');

  const combinedIsEndOfDay = symbolInformationArray.some(symbolInformation => symbolInformation.isEndOfDay);

  const symbolInformation: SymbolInformation = {
    name: combinedName,
    exchange: combinedExchange,
    isEndOfDay: combinedIsEndOfDay
  };

  const individualMarketRegions = symbolInformationArray.map(symbolInformation => symbolInformation.marketRegion).filter(marketRegion => typeof marketRegion === 'string');
  const combinedMarketRegion = getNumberOfUniqueValuesInArray(individualMarketRegions) === 1 ? individualMarketRegions[0] : null;

  if (combinedMarketRegion) {
    symbolInformation.marketRegion = combinedMarketRegion;
  }

  return symbolInformation;
}

function isValidSymbol(symbol: string): boolean {
  return (
    typeof symbol === 'string' &&
    !/\s/.test(symbol) &&
    !symbol.startsWith(':') &&
    !symbol.endsWith(':') &&
    !symbol.startsWith('--') &&
    !symbol.endsWith('-') &&
    !symbol.includes('::') &&
    !symbol.includes('---') &&
    !symbol.includes('-:') &&
    !symbol.includes(':--')
  );
}

async function getSymbolInformation(symbol: string): Promise<SymbolInformation> {
  if (typeof symbol !== 'string') { // TODO: for .js files that still use this
    throw new Error(`Unable to get symbol information - symbol argument is not a string: ${typeof symbol}.`);
  }

  symbol = symbol.trim().toUpperCase();

  if (!isValidSymbol(symbol)) {
    throw new Error(`Unable to get symbol information for symbol: ${symbol} - symbol is invalid.`);
  }

  const individualSymbols = splitRatioAndDifferenceSymbolsIntoParts(symbol);

  if (!individualSymbols.length) {
    throw new Error(`Unable to get symbol information for symbol: ${symbol} - unable to parse symbol possible composite symbol into valid parts.`);
  }

  const alreadyPendingSymbolInformationPromises: Promise<void>[] = [];
  const cachedSymbolInformation: Record<string, SymbolInformation> = {};
  const individualSymbolsToFetchSymbolInformationFor: string[] = [];

  for (const individualSymbol of individualSymbols) {
    const cached = getSymbolInformationFromCache(individualSymbol);

    if (cached) {
      cachedSymbolInformation[individualSymbol] = cached;
      continue;
    }

    const cachedSymbolInformationPromise = symbolInformationPromisesCache.get(individualSymbol, true);

    if (cachedSymbolInformationPromise) {
      // there's already a request for this symbol underway - await on the current
      // request's promise rather than kicking off a new request

      alreadyPendingSymbolInformationPromises.push(cachedSymbolInformationPromise);
      continue;
    }

    individualSymbolsToFetchSymbolInformationFor.push(individualSymbol);
  }

  const newSymbolInformationPromise = fetchSymbolInformation(individualSymbolsToFetchSymbolInformationFor);

  for (const individualSymbol of individualSymbolsToFetchSymbolInformationFor) {
    symbolInformationPromisesCache.set(individualSymbol, newSymbolInformationPromise);
  }

  await Promise.all([
    ...alreadyPendingSymbolInformationPromises,
    newSymbolInformationPromise
  ]);

  const symbolInformationArray: SymbolInformation[] = [];

  for (const individualSymbol of individualSymbols) {
    const previouslyCachedSymbolInformation = cachedSymbolInformation[individualSymbol];

    if (previouslyCachedSymbolInformation) {
      symbolInformationArray.push(previouslyCachedSymbolInformation);
      continue;
    }

    const newlyCachedSymbolInformation = getSymbolInformationFromCache(individualSymbol);

    if (newlyCachedSymbolInformation) {
      symbolInformationArray.push(newlyCachedSymbolInformation);
      continue;
    }

    throw new Error(`Failed to get symbol information for symbol: ${symbol} - symbol information not found for: ${individualSymbol}.`);
  }

  return mergeSymbolInformationForRatioOrDifferenceSymbol(symbolInformationArray);
}

function isCryptoSymbolInformation(symbolInformation?: Pick<SymbolInformation, 'exchange'>): boolean {
  if (!symbolInformation || typeof symbolInformation.exchange !== 'string') { // TODO: keep this for .js files
    return false;
  }

  return symbolInformation.exchange.toUpperCase().includes('CRYPT');
}

async function isCryptoSymbol(symbol: string): Promise<boolean> {
  let symbolInformation;

  try {
    symbolInformation = await getSymbolInformation(symbol);
  } catch (e) {
    window.sc3WorkbenchLogger.debug(`Error occurred while getting symbol information for symbol: ${symbol} to determine if symbol is a crypto symbol.`, e);
  }

  return isCryptoSymbolInformation(symbolInformation);
}

export function useSymbolInformation() {
  const layoutStore = useLayoutStore();

  individualSymbolsBeingViewed = individualSymbolsBeingViewed ?? computed(() => {
    if (!isValidSymbol(layoutStore.symbol)) {
      return [];
    }

    return splitRatioAndDifferenceSymbolsIntoParts(layoutStore.symbol);
  });

  symbolInformationArrayForSymbolBeingViewed = symbolInformationArrayForSymbolBeingViewed ?? symbolInformationCache.getComputedRefValuesFromRefKeys(individualSymbolsBeingViewed);

  symbolInformationForSymbolBeingViewed = symbolInformationForSymbolBeingViewed ?? computed(() => {
    if (!symbolInformationArrayForSymbolBeingViewed?.value.length) {
      return null;
    }

    const symbolInformationArray: SymbolInformation[] = [];

    for (const symbolInformation of symbolInformationArrayForSymbolBeingViewed.value) {
      if (!symbolInformation) {
        return null;
      }

      symbolInformationArray.push(symbolInformation);
    }

    return mergeSymbolInformationForRatioOrDifferenceSymbol(symbolInformationArray);
  });

  isSymbolBeingViewedNotFound = isSymbolBeingViewedNotFound ?? computed(() => {
    if (
      !individualSymbolsBeingViewed?.value.length ||
      !symbolInformationForSymbolBeingViewed
    ) {
      return false;
    }

    return !symbolInformationForSymbolBeingViewed.value;
  });

  isSymbolBeingViewedAnEODSymbolOnAnIntradayChart = isSymbolBeingViewedAnEODSymbolOnAnIntradayChart ?? computed(() => {
    if (
      !individualSymbolsBeingViewed?.value.length ||
      !symbolInformationForSymbolBeingViewed ||
      !layoutStore.period
    ) {
      return false;
    }

    return (
      !!symbolInformationForSymbolBeingViewed.value?.isEndOfDay &&
      getPeriodOptionFromPeriodValue(layoutStore.period).isIntraday
    );
  });

  return {
    getSymbolInformation,
    isCryptoSymbol,
    isCryptoSymbolInformation,
    isSymbolBeingViewedNotFound,
    isSymbolBeingViewedAnEODSymbolOnAnIntradayChart
  };
}
